import { useCallback, useMemo } from 'react';
import { useFrame } from '../utils';
import { ProductLabelCondition } from '~/lib';

enum NormalizedBool {
    TRUE = 'true',
    FALSE = 'false',
}

const normalize = (value: unknown): string | undefined => {
    // If value is "empty", we will change it to undefined to make matching easier.
    if (value === null || value === undefined || value === '') return undefined;

    // If value is a stringified boolean or a boolean, we will normalize it to lowercase string booleans.
    const isStringifiedBool =
        typeof value === 'string' &&
        (value.toLowerCase() === NormalizedBool.TRUE ||
            value.toLowerCase() === NormalizedBool.FALSE);

    if (typeof value === 'boolean') {
        return value ? NormalizedBool.TRUE : NormalizedBool.FALSE;
    } else if (isStringifiedBool) {
        return value.toLowerCase();
    }

    // Else, we'll just stringify the value and use as is.
    return String(value).toLocaleLowerCase();
};

/**
 * Aliases can be a path in the format of `parentKey.childKey.grandChildKey`. This will
 * iterate through the object properties where possible.
 */
const resolvePath = (record: Record<string, unknown>, path: string) =>
    path.split('.').reduce<unknown>((value, currentKey) => {
        if (value && typeof value === 'object' && currentKey in value)
            return value[currentKey as keyof typeof value];

        return undefined;
    }, record);

const getPropertyValue = (record: Record<string, unknown>, alias: string) => {
    // Split trimmed semicolon-separated aliases.
    const aliases = alias.replace(/\s/g, '').split(';');

    // We will only use the first alias that resolves to a value in our record.
    return aliases.map((alias) => resolvePath(record, alias)).find((value) => value !== undefined);
};

/**
 * TODO(IMC-1671): Align SalesforceProduct, Product, and TProductDetails types.
 * Reading the structural shape accepts all three, which the nominal types do not.
 */
type ProductLike = {
    id?: string;
    [key: string]: unknown;
};

export const useProductLabels = (product: ProductLike) => {
    const { data: frame } = useFrame();
    const definedLabels = frame.productSettings?.productLabels;

    const matchCondition = useCallback((condition: ProductLabelCondition) => {
        const { productField, operator } = condition;

        /**
         * Normalizing our values and target values will allow easier comparison between differently typed variables.
         * We normalize by doing the following:
         * 1. Strings will be as they are. (Ex.: "curly" contains "url")
         * 2. All numbers will be turned into strings (Ex.: "1.23" is "1.23")
         * 3. Booleans will be changed to lowercase strings. (Ex.: "false" is not "true")
         */
        const value = normalize(getPropertyValue(product, productField.alias));
        const targetValue = normalize(condition.value);

        switch (operator) {
            case 'is':
                return targetValue === value;

            case 'is not':
                return targetValue !== value;

            case 'contains': {
                // We cannot do a "contains" expression if either value is empty.
                if (!targetValue || !value) return false;

                return value.includes(targetValue);
            }

            default:
                return false;
        }
    }, []);

    const productLabels = useMemo(
        () =>
            definedLabels?.filter((label) => {
                const { conditions, displayText } = label;

                // A display text is required.
                if (!displayText) return false;

                // If no conditions are set, we will show the label always.
                if (!conditions.length) return true;

                // We will separate AND and OR operations for easier resolution.
                const { or, and } = conditions.reduce<{
                    or: ProductLabelCondition[];
                    and: ProductLabelCondition[];
                }>(
                    (set, condition) => {
                        const { logicalOperator } = condition;

                        if (logicalOperator === 'OR') {
                            set.or.push(condition);
                        } else {
                            // AND is the default logical operator.
                            set.and.push(condition);
                        }

                        return set;
                    },
                    { or: [], and: [] },
                );

                // For OR, as long as one is met, we will show the label.
                if (or.some(matchCondition)) return true;

                // Otherwise, for AND, all conditions need to be met to show the label.
                return !!and.length && and.every(matchCondition);
            }),
        [product.id],
    );

    return productLabels;
};
